/**
 * apiFetch - Authenticated fetch bound to the sign-in that started it:
 *  1. Attaches Authorization: Bearer <access>, refreshing first when it is about to expire
 *  2. On 401, refreshes once and replays the request
 *  3. Never replays or returns a response after the sign-in changes
 *
 * Used by AuthContext, push notifications and the cash-entry API. The other `use*Api` hooks still
 * build their requests on `useAuth().getAccessToken()` and raw `fetch()`.
 */
import { getApiBaseUrl } from "./config";
import {
  loadSession,
  clearSession,
  refreshAccessToken,
  isSameSession,
  holdsTokens,
  TOKEN_REFRESH_MARGIN_MS,
  SessionExpiredError,
  type AuthSession,
} from "./authApi";

function requireSameSession(session: AuthSession): void {
  if (!isSameSession(session)) {
    throw new DOMException("La session a changé, veuillez réessayer", "AbortError");
  }
}

export type ApiTransport = (url: string, init: RequestInit) => Promise<Response>;

export async function apiFetch(
  path: string,
  init: RequestInit = {},
  send: ApiTransport = (url, options) => fetch(url, options),
): Promise<Response> {
  init.signal?.throwIfAborted();
  const session = loadSession();
  if (!session?.tokens?.access) {
    clearSession();
    throw new SessionExpiredError();
  }
  const { headers: extraHeaders, ...rest } = init;
  const headers = new Headers(extraHeaders);
  if (!(rest.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  let access = session.tokens.access;
  if (Date.now() > session.tokens.expiresAt - TOKEN_REFRESH_MARGIN_MS) {
    const refreshed = await refreshAccessToken();
    if (!refreshed?.access) {
      if (loadSession()) requireSameSession(session);
      throw new SessionExpiredError();
    }
    requireSameSession(session);
    access = refreshed.access;
  }
  requireSameSession(session);
  rest.signal?.throwIfAborted();
  headers.set("Authorization", `Bearer ${access}`);
  const response = await send(`${getApiBaseUrl()}${path}`, { headers, ...rest });
  requireSameSession(session);
  rest.signal?.throwIfAborted();
  if (response.status !== 401) return response;

  const refreshed = await refreshAccessToken();
  // Refresh failures clear only their own session. Preserve a new sign-in.
  if (!isSameSession(session)) {
    if (loadSession()) requireSameSession(session);
    return response;
  }
  if (!refreshed?.access) return response;
  rest.signal?.throwIfAborted();
  const retryHeaders = new Headers(headers);
  retryHeaders.set("Authorization", `Bearer ${refreshed.access}`);
  const retry = await send(`${getApiBaseUrl()}${path}`, { headers: retryHeaders, ...rest });
  requireSameSession(session);
  if (retry.status === 401 && holdsTokens(loadSession(), refreshed)) clearSession();
  return retry;
}
